/**
 * What `<html>` currently says the theme is — the effective colour scheme and the shop look —
 * as a React subscription.
 *
 * Both attributes are written by other code (`applyTheme` for the scheme, `applyShopTheme` for
 * the shop) and the stylesheet resolves from them directly. Anything else that needs the same
 * answer — MUI's palette, which cannot read a CSS variable — should read the attributes rather
 * than re-derive them from the toggle and the OS, because the attributes already include the
 * shop's own default scheme and the toggle alone does not. One source, no drift.
 *
 * `useSyncExternalStore` over a `MutationObserver`, so a change to either attribute re-renders
 * the subscriber in the same frame the stylesheet repaints.
 */

import { useSyncExternalStore } from 'react';

export type DocumentTheme = {
  readonly mode: 'light' | 'dark';
  /**
   * `'neutral'` on a page with no shop applied — the signed-out state, which is a look of its
   * own rather than an absence. `undefined` only before anything has written the attribute.
   * Resolve it with `themeLookFor`, never `shopThemeFor`: the shop-only lookup degrades an
   * unknown code to the default, which would put OriTech's colours back on a neutral page.
   */
  readonly shopThemeCode: string | undefined;
};

const systemDark = () => window.matchMedia('(prefers-color-scheme: dark)');

function read(): DocumentTheme {
  const root = document.documentElement;
  const explicit = root.dataset.theme;

  return {
    mode: explicit === 'dark' || explicit === 'light' ? explicit : systemDark().matches ? 'dark' : 'light',
    shopThemeCode: root.dataset.shopTheme,
  };
}

// A stable snapshot object per distinct value, or `useSyncExternalStore` sees a new reference
// on every call and re-renders forever. Read lazily, not at module load: this module is reached
// by anything that imports the package, including tests and scripts with no `document`.
let snapshot: DocumentTheme | undefined;

function current(): DocumentTheme {
  const next = read();

  if (next.mode !== snapshot?.mode || next.shopThemeCode !== snapshot.shopThemeCode) {
    snapshot = next;
  }

  return snapshot;
}

const SERVER: DocumentTheme = { mode: 'light', shopThemeCode: undefined };

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-shop-theme'] });

  const media = systemDark();
  media.addEventListener('change', onChange);

  return () => {
    observer.disconnect();
    media.removeEventListener('change', onChange);
  };
}

export function useDocumentTheme(): DocumentTheme {
  return useSyncExternalStore(subscribe, current, () => SERVER);
}
